import React, { useState, useEffect } from "react";
import { Form, Field } from "react-final-form";
import { Link } from "react-router-dom";
import {
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Typography,
  Grid2,
  Alert,
} from "@mui/material"; // Import Checkbox, FormControlLabel, and CircularProgress
import { useApi } from "../../utils/api";
import moment from "moment";
import { useUserInfo } from "../../utils/user";
import LoadingButton from "@mui/lab/LoadingButton";

const PLATFORM_OWNER_USER_ID = import.meta.env.VITE_PLATFORM_OWNER_USER_ID;

const AddClient = ({ onClose, onAddClient }) => {
  const { id, name, stripeAccountId, stripeIsSetup, paymentsProEnabled } =
    useUserInfo();
  const isPlatformUser = id === PLATFORM_OWNER_USER_ID;
  const {
    addClient,
    sendEmail,
    getProducts,
    postUserProducts,
    startConversation,
  } = useApi();
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(true); // Loading state
  const [apiLoading, setApiLoading] = useState(false);
  const [error, setError] = useState(null);
  const [missingDescriptionPrices, setMissingDescriptionPrices] = useState([]);
  const [missingQuantityPrices, setMissingQuantityPrices] = useState([]);
  useEffect(() => {
    const fetchProducts = async () => {
      const accountId = isPlatformUser ? undefined : stripeAccountId;
      const res = await getProducts(accountId);
      setProducts(res || []);
      const missingDescriptions = [];
      const missingQuantities = [];
      (res || []).forEach((product) => {
        product.prices.forEach((price) => {
          if (!price.metadata?.description) {
            missingDescriptions.push({
              productName: product.name,
              priceId: price.priceId,
            });
          }
          const quantityValue = price.metadata?.quantity;
          if (
            !price.recurring &&
            (quantityValue === undefined || quantityValue === "")
          ) {
            missingQuantities.push({
              productName: product.name,
              priceId: price.priceId,
            });
          }
        });
      });
      setMissingDescriptionPrices(missingDescriptions);
      setMissingQuantityPrices(missingQuantities);
      setLoading(false); // Set loading to false after fetching
    };
    if ((stripeIsSetup && paymentsProEnabled) || isPlatformUser) {
      fetchProducts();
    } else {
      setLoading(false);
    }
  }, [getProducts, stripeIsSetup, paymentsProEnabled, isPlatformUser, stripeAccountId]);

  const handleCheckboxChange = (priceId) => {
    setSelectedProducts((prevSelected) => {
      if (prevSelected.includes(priceId)) {
        return prevSelected.filter((id) => id !== priceId);
      } else {
        return [...prevSelected, priceId];
      }
    });
  };

  const validate = (values) => {
    const errors = {};

    if (!values.name || values.name.trim() === "") {
      errors.name = "Name is required";
    } else if (values.name.trim().length < 2) {
      errors.name = "Name must be at least 2 characters";
    }

    if (!values.email || values.email.trim() === "") {
      errors.email = "Email is required";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(values.email.trim())) {
        errors.email = "Please enter a valid email address";
      }
    }

    return errors;
  };

  const onSubmit = async (v) => {
    if ((stripeIsSetup && paymentsProEnabled || isPlatformUser) && !selectedProducts.length) {
      setError("Please select at least one product");
      return;
    }
    setApiLoading("Creating Client...");
    try {
      const resp = await addClient({
        ...v,
        coachId: id,
        createdDate: moment().unix(),
      });
      if (!resp.id) {
        if (resp.message) {
          setError(resp.message);
        } else {
          setError("Failed to create client");
        }
        setApiLoading(false);
        return;
      }
      setApiLoading("Sending Email...");
      await sendEmail({
        emailType: "SET-PASSWORD",
        name: v.name,
        coachName: name,
        coachId: id,
        userId: btoa(v.email),
        sendEmail: v.email,
        resetLink: resp.resetLink,
      });
      if (stripeIsSetup || isPlatformUser) {
        setApiLoading("Assigning Products...");
        // Prepare items for postUserProducts
        const items = selectedProducts.map((priceId) => {
          const product = products.find((p) =>
            p.prices.some((price) => price.priceId === priceId)
          );
          return {
            productId: product.productId,
            priceId: priceId,
          };
        });

        // Submit selected products (platform user: no stripeAccountId)
        await postUserProducts({
          items,
          type: "assigned",
          userId: resp.id,
          stripeAccountId: isPlatformUser ? undefined : stripeAccountId,
        });
      }

      setApiLoading("Creating Conversation...");
      await startConversation({
        participantIds: [id, resp.id],
        name: v.name,
      });

      onClose();
      onAddClient();
      setApiLoading(false);
    } catch (error) {
      console.error("Failed to create client:", error);
      setError(
        error.message
          ? JSON.parse(error.message).message
          : "Failed to create client"
      );
      setApiLoading(false);
      return;
    }
  };

  const handleClose = (event, reason) => {
    // Allow closing if no products are selected
    if (selectedProducts.length === 0) {
      onClose();
    }
    // Prevent closing via backdrop click or ESC if products are selected
    if (
      selectedProducts.length > 0 &&
      (reason === "backdropClick" || reason === "escapeKeyDown")
    ) {
      return;
    }
  };

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={handleClose}>
      <DialogTitle>Add Client</DialogTitle>
      {missingDescriptionPrices.length > 0 && (
        <Alert severity="error" sx={{ mx: 3, mb: 1 }}>
          Some prices are missing description metadata. Update them on the{" "}
          <Link to="/manage-products" style={{ color: "inherit" }}>
            Manage Products
          </Link>{" "}
          page before assigning.
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      <Form
        onSubmit={onSubmit}
        validate={validate}
        render={({ handleSubmit }) => (
          <>
            <DialogContent>
              <Grid2>
                <Field
                  name="name"
                  render={({ input, meta }) => (
                    <TextField
                      label="Name"
                      size="small"
                      sx={{ mb: 2, mt: 1 }}
                      fullWidth
                      required
                      error={meta.touched && meta.error}
                      helperText={meta.touched && meta.error}
                      {...input}
                    />
                  )}
                />
                <Field
                  name="email"
                  render={({ input, meta }) => (
                    <TextField
                      label="Email"
                      type="email"
                      size="small"
                      fullWidth
                      required
                      error={meta.touched && meta.error}
                      helperText={meta.touched && meta.error}
                      {...input}
                    />
                  )}
                />
              </Grid2>
              {((stripeIsSetup && paymentsProEnabled) || isPlatformUser) && (
                <>
                  <h3>Select Products *</h3>
                </>
              )}
              {loading ? (
                <CircularProgress width="100%" /> // Show loader while fetching products
              ) : (
                <>
                  {products.map((product) => (
                    <div key={product.productId}>
                      <h4>{product.name}</h4>
                      {product.prices.map((price) => (
                        <FormControlLabel
                          key={price.priceId}
                          sx={{
                            width: "100%",
                            margin: 0,
                            "& .MuiFormControlLabel-label": {
                              width: "100%",
                            },
                          }}
                          control={
                            <Checkbox
                              checked={selectedProducts.includes(price.priceId)}
                              onChange={() =>
                                handleCheckboxChange(price.priceId)
                              }
                              color="primary"
                            />
                          }
                          label={
                            <Grid2 container justifyContent="space-between">
                              <Grid2 size={10}>
                                <Typography variant="body">
                                  {price.metadata.description}
                                </Typography>
                              </Grid2>
                              <Grid2 size={2}>
                                <Typography variant="body1" fontWeight="bold">
                                  ${(price.unitAmount / 100).toFixed(2)}
                                </Typography>
                              </Grid2>
                            </Grid2>
                          }
                        />
                      ))}
                    </div>
                  ))}
                </>
              )}
            </DialogContent>
            <DialogActions
              sx={{
                "& .MuiLoadingButton-loadingIndicator": {
                  position: "relative", // Allows the button to widen based on text
                  whiteSpace: "nowrap", // Prevents text from wrapping to a new line
                },
              }}
            >
              <LoadingButton
                loading={!!apiLoading}
                loadingIndicator={apiLoading}
                onClick={handleSubmit}
                variant="contained"
              >
                Add Client
              </LoadingButton>
              <Button onClick={onClose}>Cancel</Button>
            </DialogActions>
          </>
        )}
      />
    </Dialog>
  );
};

export default AddClient;
